'use client';

import AddressSearch from '@/components/AddressSearch';
import { ExtractedAddress } from '@/components/AddressSearch/types';
import { Dictionary } from '@/lib/get-dictionary';
import { colors, Text, TextFieldStatus } from '@/lib/mx-asurion-ui-react';
import clsx from 'clsx';
import React, { useCallback, useState } from 'react';
import AddressInput from '../AddressInput/AddressInput';
import { GridFormLayoutProps } from '../GridFormLayout/GridFormLayout';
import { CustomLink } from './elements';

interface AddressSelectProps {
  containerClassName?: string;
  defaultValue?: string;
  dictionary?: Dictionary;
  disabled?: boolean;
  divider?: boolean;
  fieldStatus?: TextFieldStatus;
  gridLayoutProps?: GridFormLayoutProps;
  helperText?: string;
  inputsContainerClassName?: string;
  labelText?: string;
  linkText?: string;
  onAddressSelected: (address: ExtractedAddress | undefined) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
}

export const AddressSelect: React.FC<AddressSelectProps> = ({
  containerClassName,
  defaultValue,
  dictionary,
  disabled,
  divider,
  fieldStatus,
  gridLayoutProps,
  helperText,
  inputsContainerClassName,
  labelText,
  linkText,
  onAddressSelected,
  onBlur,
}) => {
  const [searchedAddress, setSearchedAddress] = useState<
    ExtractedAddress | undefined
  >();
  const [selectedAddress, setSelectedAddress] = useState<
    ExtractedAddress | undefined
  >();
  const [searchAddressEnabled, setSearchAddressEnabled] = useState(true);
  const [inputAddressEnabled, setInputAddressEnabled] = useState(false);

  const handleAddressChange = useCallback(
    (newAddress: ExtractedAddress | undefined) => {
      setSelectedAddress(newAddress);
      onAddressSelected(newAddress);
    },
    [onAddressSelected],
  );

  return (
    <>
      <div className={clsx(containerClassName)}>
        <AddressSearch
          onAddressSelect={handleAddressChange}
          defaultValue={defaultValue}
          disabled={disabled || !searchAddressEnabled}
          labelText={labelText}
          fieldStatus={fieldStatus}
          onBlur={onBlur}
        />
        <Text
          data-testid="helper-text"
          size={1}
          color={
            fieldStatus === 'default' ? colors.neutralDeeper : colors.errorDeep
          }
          style={{
            paddingLeft: '.75rem',
            paddingTop: '.45rem',
            display: 'inline-block',
          }}
        >
          {helperText}
          {!!linkText ? ' ' : ''}
          <CustomLink
            textcolor="#6E767D"
            href="#"
            onClick={() => {
              if (!inputAddressEnabled) {
                setInputAddressEnabled(true);
                setSearchAddressEnabled(false);
                setSearchedAddress(selectedAddress);
              } else {
                setInputAddressEnabled(false);
                setSearchAddressEnabled(true);
                handleAddressChange(searchedAddress);
              }
            }}
          >
            {linkText}
          </CustomLink>
        </Text>
      </div>
      {inputAddressEnabled && (
        <AddressInput
          containerClassName={inputsContainerClassName}
          onAddressCompleted={handleAddressChange}
          address={searchedAddress}
          disabled={disabled}
          gridLayoutProps={gridLayoutProps}
          divider={divider ?? false}
          dictionary={dictionary}
        />
      )}
    </>
  );
};

export default AddressSelect;
